import { SearchBarCase, SearchProvider, SearchResults, useSearchStore } from '@local/search';
import { AnvilProvider, ButtonLink, Flex, Text } from '@servicetitan/anvil2';
import IconGnavMarketingActive from '@servicetitan/anvil2/assets/icons/st/gnav_marketing_active.svg';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import { FC, Fragment } from 'react';
import { NavLink } from 'react-router-dom';
import Logo from '../assets/logo.svg';
import * as Styles from './page-header.module.less';
import { ProfileMenu } from './profile-menu';

export interface PageHeaderProps {
    className?: string;
    showSearch?: boolean;
}

const PageHeaderContent: FC<{ className?: string; showSearch: boolean }> = observer(
    ({ className, showSearch }) => {
        const searchStore = useSearchStore();
        const shouldShowOverlay =
            showSearch && searchStore.isSearchResultsVisible && searchStore.hideOnBlur;

        return (
            <Fragment>
                <Flex
                    alignItems="center"
                    justifyContent="space-between"
                    gap="4"
                    className={classNames(Styles.pageHeader, className)}
                    data-cy="ish-page-header"
                >
                    <NavLink to="/" className={Styles.homeLink}>
                        <Flex alignItems="center" gap="2" className="p-inline-end-3">
                            <img src={Logo} alt="Logo" />
                            <Text el="h2" size="medium" variant="headline" className="m-0">
                                Intelligent Support Hub
                            </Text>
                        </Flex>
                    </NavLink>
                    {showSearch && (
                        <Flex grow={1} className={Styles.searchBarContainer}>
                            <Flex direction="column" alignSelf="stretch" flex={1}>
                                <SearchBarCase
                                    hideOnBlur
                                    size="small"
                                    placeholder="Search"
                                    dataCy="ish-header-search-input"
                                    dataPendo="ish-header-search-input"
                                />
                                <AnvilProvider themeData={{ mode: 'light' }}>
                                    <div
                                        className={Styles.searchResultsDropdown}
                                        onMouseDown={e => {
                                            // Prevent blur when clicking on results
                                            e.preventDefault();
                                        }}
                                    >
                                        <SearchResults />
                                    </div>
                                </AnvilProvider>
                            </Flex>
                        </Flex>
                    )}
                    <Flex gap="4" alignItems="center">
                        {showSearch && (
                            <ButtonLink
                                icon={{ before: IconGnavMarketingActive }}
                                href="https://docs.google.com/forms/d/14ECaLOOF3NkBhMmcKxH7PeKlvx_Vd7BYabhMYx9GgrA/edit"
                                target="_blank"
                                size="small"
                                appearance="ghost"
                                className={Styles.feedbackButton}
                                data-cy="ish-page-header-feedback-button"
                                data-pendo="ish-page-header-feedback-button"
                            >
                                Feedback
                            </ButtonLink>
                        )}
                        <ProfileMenu />
                    </Flex>
                </Flex>
                {shouldShowOverlay && (
                    <div
                        className={Styles.searchOverlay}
                        onClick={() => {
                            searchStore.setIsFocused(false);
                        }}
                        data-cy="ish-search-overlay"
                    />
                )}
            </Fragment>
        );
    }
);

export const PageHeader: FC<PageHeaderProps> = ({ className, showSearch = true }) => {
    return (
        <AnvilProvider themeData={{ mode: 'dark' }}>
            <SearchProvider>
                <PageHeaderContent className={className} showSearch={showSearch} />
            </SearchProvider>
        </AnvilProvider>
    );
};
